/* eslint-disable max-lines */
/**
 * useOrejaSession - Core orchestrator hook for a single Oreja session.
 *
 * Manages the full lifecycle: recording -> transcribing -> clarifying ->
 * answering -> proposing -> reviewing -> refining -> committing -> committed.
 *
 * @module useOrejaSession
 */

import { useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { callEdgeFunctionJson } from '@/lib/edge-function-client';
import { useToast } from '@/hooks/use-toast';
import { trackFeatureEvent, FeatureEvent } from '@/lib/feature-events';
import {
  resolveInputToText,
  fetchExistingTasks,
  normalizeProposals,
} from '@/lib/oreja/session-helpers';
import type {
  OrejaSession,
  OrejaPhase,
  OrejaClarificationQuestion,
  OrejaProposals,
  OrejaCommittedChange,
  OrejaFailedCommit,
  OrejaUpdatedTaskProposal,
  OrejaNewTaskProposal,
} from '@/lib/oreja/types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Voice or text input from the user. */
export type OrejaInput = { type: 'voice'; blob: Blob } | { type: 'text'; text: string };

/** Parameters for starting a new session. */
export interface OrejaStartParams {
  clientId: string;
  projectId: string;
  clickupListId: string;
  clientName: string;
  projectName: string;
  userName: string;
  input: OrejaInput;
}

/** Options for the hook. */
export interface UseOrejaSessionOptions {
  /** Resume an existing session. Omit to create a new one. */
  sessionId?: string;
  /** Organization ID (required for DB inserts). */
  organizationId: string;
  /** Current user ID (required for DB inserts). */
  userId: string;
}

/** Return value of useOrejaSession. */
export interface UseOrejaSessionReturn {
  session: OrejaSession | null;
  phase: OrejaPhase;
  transcript: string | null;
  questions: OrejaClarificationQuestion[] | null;
  proposals: OrejaProposals | null;
  error: string | null;
  isProcessing: boolean;
  start: (params: OrejaStartParams) => Promise<void>;
  sendAnswers: (input: OrejaInput, radioContext?: string) => Promise<void>;
  /** Switches to refining recording UI (call refine() after user provides input). */
  startRefine: () => void;
  refine: (input: OrejaInput) => Promise<void>;
  /** Skip clarification and go straight to proposals. */
  skipClarification: (radioContext?: string) => Promise<void>;
  /** Update proposals locally (for inline editing). */
  updateProposals: (updater: (prev: OrejaProposals) => OrejaProposals) => void;
  commit: (selectedUpdatedIndexes: number[], selectedNewIndexes: number[]) => Promise<void>;
  /** Tasks that failed during the most recent commit attempt. */
  failedCommits: OrejaFailedCommit[];
  /** Retry only the previously failed commits. */
  retryFailed: () => Promise<void>;
  /** Whether the current error state supports retrying without starting over. */
  canRetry: boolean;
  /** Retry the last failed operation based on the current phase and available state. */
  retry: () => Promise<void>;
  reset: () => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Core hook for managing a single Oreja session lifecycle.
 *
 * @param options - Configuration including optional sessionId, organizationId, and userId.
 * @returns Session state and action callbacks.
 */
export function useOrejaSession(options: UseOrejaSessionOptions): UseOrejaSessionReturn {
  const { sessionId: initialSessionId, organizationId, userId } = options;
  const { toast } = useToast();

  const [session, setSession] = useState<OrejaSession | null>(null);
  const [phase, setPhase] = useState<OrejaPhase>('recording');
  const [transcript, setTranscript] = useState<string | null>(null);
  const [questions, setQuestions] = useState<OrejaClarificationQuestion[] | null>(null);
  const [proposals, setProposals] = useState<OrejaProposals | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [failedCommits, setFailedCommits] = useState<OrejaFailedCommit[]>([]);

  const sessionIdRef = useRef<string | null>(initialSessionId ?? null);
  const clickupListIdRef = useRef<string | null>(null);
  const clientNameRef = useRef<string>('');
  const projectNameRef = useRef<string>('');
  const userNameRef = useRef<string>('');
  const refinementInputsRef = useRef<string[]>([]);

  /** Resets all state back to idle. */
  const reset = useCallback(() => {
    if (phase && phase !== 'committed' && phase !== 'recording') {
      trackFeatureEvent('oreja', FeatureEvent.OREJA.SESSION_ABANDONED, {
        last_phase: phase,
      });
    }
    setSession(null);
    setPhase('recording');
    setTranscript(null);
    setQuestions(null);
    setProposals(null);
    setError(null);
    setIsProcessing(false);
    setFailedCommits([]);
    sessionIdRef.current = null;
    clickupListIdRef.current = null;
    clientNameRef.current = '';
    projectNameRef.current = '';
    userNameRef.current = '';
    refinementInputsRef.current = [];
  }, [phase]);

  /** Starts a new session: creates DB row, resolves input, runs clarify phase. */
  const start = useCallback(
    async (params: OrejaStartParams) => {
      const { clientId, projectId, clickupListId, clientName, projectName, userName, input } =
        params;
      setError(null);
      setIsProcessing(true);

      try {
        const sessionId = initialSessionId ?? crypto.randomUUID();
        sessionIdRef.current = sessionId;
        clickupListIdRef.current = clickupListId;
        clientNameRef.current = clientName;
        projectNameRef.current = projectName;
        userNameRef.current = userName;

        const { data: sessionRow, error: insertError } = await supabase
          .from('oreja_sessions')
          .insert({
            id: sessionId,
            organization_id: organizationId,
            user_id: userId,
            client_id: clientId,
            project_id: projectId,
            clickup_list_id: clickupListId,
            phase: 'transcribing',
            input_mode: input.type,
          })
          .select()
          .single();

        if (insertError) throw new Error(`Failed to create session: ${insertError.message}`);

        setSession(sessionRow as unknown as OrejaSession);
        setPhase('transcribing');

        trackFeatureEvent('oreja', FeatureEvent.OREJA.SESSION_STARTED, {
          input_mode: input.type,
          project_id: clickupListIdRef.current,
        });

        const resolvedText = await resolveInputToText(sessionId, input, 'input');
        setTranscript(resolvedText);

        const existingTasks = await fetchExistingTasks(clickupListId);
        // Stay on 'analyzing' (progress stepper) until questions come back
        setPhase('analyzing' as OrejaPhase);

        const projectContext = {
          clientName: clientNameRef.current,
          projectName: projectNameRef.current,
          userName: userNameRef.current,
          listId: clickupListId,
        };

        const clarifyResponse = await callEdgeFunctionJson<{
          success: boolean;
          error?: string;
          data?: { questions: OrejaClarificationQuestion[] };
        }>('oreja-analyze', {
          body: {
            phase: 'clarify',
            sessionId,
            transcript: resolvedText,
            existingTasks,
            projectContext,
          },
        });

        if (!clarifyResponse.success) {
          throw new Error(clarifyResponse.error ?? 'Clarification phase failed');
        }

        const questions = clarifyResponse.data?.questions ?? [];
        setQuestions(questions);

        await supabase
          .from('oreja_sessions')
          .update({ phase: 'clarifying', transcript: resolvedText, questions })
          .eq('id', sessionId);

        setPhase('clarifying');
        trackFeatureEvent('oreja', FeatureEvent.OREJA.PHASE_ENTERED, { phase: 'clarifying' });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unexpected error occurred');
        console.error('[Oreja] start() error:', err);
      } finally {
        setIsProcessing(false);
      }
    },
    [initialSessionId, organizationId, userId],
  );

  /** Sends answers to clarification questions and triggers the propose phase. */
  const sendAnswers = useCallback(
    async (input: OrejaInput, radioContext?: string) => {
      const sessionId = sessionIdRef.current;
      const clickupListId = clickupListIdRef.current;
      if (!sessionId || !clickupListId) {
        setError('No active session');
        return;
      }

      setError(null);
      setIsProcessing(true);
      try {
        setPhase('answering');
        let answersText = await resolveInputToText(sessionId, input, 'answers');
        if (radioContext) {
          answersText = `${radioContext}\n\n${answersText}`;
        }
        const existingTasks = await fetchExistingTasks(clickupListId);
        setPhase('proposing');

        const projectContext = {
          clientName: clientNameRef.current,
          projectName: projectNameRef.current,
          userName: userNameRef.current,
          listId: clickupListIdRef.current!,
        };

        const proposeResponse = await callEdgeFunctionJson<{
          success: boolean;
          error?: string;
          data?: OrejaProposals;
        }>('oreja-analyze', {
          body: {
            phase: 'propose',
            sessionId,
            transcript,
            answers: answersText,
            existingTasks,
            projectContext,
          },
        });

        if (!proposeResponse.success) {
          throw new Error(proposeResponse.error ?? 'Proposal generation failed');
        }

        const proposals = normalizeProposals(proposeResponse.data);
        setProposals(proposals);
        await supabase
          .from('oreja_sessions')
          .update({ phase: 'reviewing', answers: answersText, proposals })
          .eq('id', sessionId);
        setPhase('reviewing');
        trackFeatureEvent('oreja', FeatureEvent.OREJA.PHASE_ENTERED, { phase: 'reviewing' });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unexpected error occurred');
        console.error('[Oreja] sendAnswers() error:', err);
      } finally {
        setIsProcessing(false);
      }
    },
    [transcript],
  );

  /** Skips clarification questions and goes straight to proposal generation. */
  const skipClarification = useCallback(
    async (radioContext?: string) => {
      const sessionId = sessionIdRef.current;
      const clickupListId = clickupListIdRef.current;
      if (!sessionId || !clickupListId) {
        setError('No active session');
        return;
      }

      setError(null);
      setIsProcessing(true);
      try {
        trackFeatureEvent('oreja', FeatureEvent.OREJA.CLARIFICATION_SKIPPED, {
          had_radio_selections: !!radioContext,
        });

        setPhase('proposing');

        const existingTasks = await fetchExistingTasks(clickupListId);

        const projectContext = {
          clientName: clientNameRef.current,
          projectName: projectNameRef.current,
          userName: userNameRef.current,
          listId: clickupListIdRef.current!,
        };

        const proposeResponse = await callEdgeFunctionJson<{
          success: boolean;
          error?: string;
          data?: OrejaProposals;
        }>('oreja-analyze', {
          body: {
            phase: 'clarify',
            sessionId,
            transcript,
            skip_clarification: true,
            radio_context: radioContext ?? '',
            existingTasks,
            projectContext,
          },
        });

        if (!proposeResponse.success) {
          throw new Error(proposeResponse.error ?? 'Skip-clarification proposal generation failed');
        }

        const generatedProposals = normalizeProposals(proposeResponse.data);
        setProposals(generatedProposals);

        await supabase
          .from('oreja_sessions')
          .update({ phase: 'reviewing', proposals: generatedProposals })
          .eq('id', sessionId);

        setPhase('reviewing');
        trackFeatureEvent('oreja', FeatureEvent.OREJA.PHASE_ENTERED, { phase: 'reviewing' });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unexpected error occurred');
        console.error('[Oreja] skipClarification() error:', err);
      } finally {
        setIsProcessing(false);
      }
    },
    [transcript],
  );

  /** Switches to the refine recording UI without triggering an API call. */
  const startRefine = useCallback(() => {
    setPhase('refining');
    setError(null);
  }, []);

  /** Update proposals locally for inline editing. */
  const updateProposals = useCallback((updater: (prev: OrejaProposals) => OrejaProposals) => {
    setProposals((prev) => (prev ? updater(prev) : prev));
  }, []);

  /** Refines existing proposals based on additional user input. */
  const refine = useCallback(
    async (input: OrejaInput) => {
      const sessionId = sessionIdRef.current;
      const clickupListId = clickupListIdRef.current;
      if (!sessionId || !clickupListId) {
        setError('No active session');
        return;
      }

      setError(null);
      setIsProcessing(true);
      try {
        setPhase('refining');
        const refinementText = await resolveInputToText(sessionId, input, `refine-${Date.now()}`);
        const existingTasks = await fetchExistingTasks(clickupListId);

        const projectContext = {
          clientName: clientNameRef.current,
          projectName: projectNameRef.current,
          userName: userNameRef.current,
          listId: clickupListIdRef.current!,
        };

        const refineResponse = await callEdgeFunctionJson<{
          success: boolean;
          error?: string;
          data?: OrejaProposals;
        }>('oreja-analyze', {
          body: {
            phase: 'refine',
            sessionId,
            transcript,
            proposals,
            refinementInput: refinementText,
            existingTasks,
            projectContext,
          },
        });

        if (!refineResponse.success) {
          throw new Error(refineResponse.error ?? 'Refinement failed');
        }

        const refined = normalizeProposals(refineResponse.data);
        setProposals(refined);
        refinementInputsRef.current = [...refinementInputsRef.current, refinementText];
        await supabase
          .from('oreja_sessions')
          .update({
            phase: 'reviewing',
            proposals: refined,
            refinement_inputs: refinementInputsRef.current,
          })
          .eq('id', sessionId);
        setPhase('reviewing');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unexpected error occurred');
        console.error('[Oreja] refine() error:', err);
      } finally {
        setIsProcessing(false);
      }
    },
    [transcript, proposals],
  );

  /**
   * Pushes a single updated-task proposal to ClickUp.
   *
   * @param p - The updated task proposal to push
   * @returns The committed change record on success
   */
  async function pushUpdatedTask(p: OrejaUpdatedTaskProposal): Promise<OrejaCommittedChange> {
    const statusChange = p.field_changes.find((fc) => fc.field === 'status');
    if (statusChange?.to) {
      await callEdgeFunctionJson('clickup-sync/push-status', {
        body: { clickupTaskId: p.clickup_task_id, newStatus: statusChange.to },
      });
    }
    const detailChanges = p.field_changes.filter((fc) => fc.field !== 'status');
    if (detailChanges.length > 0 || p.enhanced_description) {
      const updates: Record<string, unknown> = {};
      for (const fc of detailChanges) {
        updates[fc.field] = fc.to;
      }
      if (p.enhanced_description) {
        updates.description = p.enhanced_description;
      }
      await callEdgeFunctionJson('clickup-sync/push-details', {
        body: { clickup_task_id: p.clickup_task_id, updates },
      });
    }
    if (p.comment_to_add) {
      await callEdgeFunctionJson('clickup-tasks/comment', {
        body: { clickupTaskId: p.clickup_task_id, comment: p.comment_to_add },
      });
    }
    return {
      type: 'update',
      clickup_task_id: p.clickup_task_id,
      clickup_url: `https://app.clickup.com/t/${p.clickup_task_id}`,
      task_title: p.task_title,
      changes_summary: p.field_changes
        .map((fc) => `${fc.field}: ${fc.from ?? '(none)'} -> ${fc.to ?? '(none)'}`)
        .join(', '),
      pushed_at: new Date().toISOString(),
    };
  }

  /**
   * Pushes a single new-task proposal to ClickUp.
   *
   * @param p - The new task proposal to push
   * @returns The committed change record on success
   */
  const pushNewTask = useCallback(
    async (p: OrejaNewTaskProposal): Promise<OrejaCommittedChange> => {
      const r = await callEdgeFunctionJson<{
        task: { id: string; url: string };
        was_reparented?: boolean;
      }>('clickup-tasks/create', {
        body: {
          name: p.title,
          description: p.description,
          status: p.suggested_status,
          assignee: p.suggested_assignee,
          priority: p.priority,
          start_date: p.suggested_start_date,
          due_date: p.suggested_due_date,
          time_estimate: p.time_estimate,
          list_id: p.clickup_list_id ?? clickupListIdRef.current,
          parent_task_id: p.parent_task_id,
        },
      });
      if (r.was_reparented) {
        toast({
          title: 'Task was re-parented',
          description: 'The task was automatically re-parented to stay within the depth limit.',
        });
      }
      return {
        type: 'new_task',
        clickup_task_id: r.task.id,
        clickup_url: r.task.url,
        task_title: p.title,
        changes_summary: `New task created: ${p.title}`,
        pushed_at: new Date().toISOString(),
      };
    },
    [toast],
  );

  /** Commits selected proposals by pushing changes to ClickUp. */
  const commit = useCallback(
    async (selectedUpdatedIndexes: number[], selectedNewIndexes: number[]) => {
      const sessionId = sessionIdRef.current;
      if (!sessionId || !proposals) {
        setError('No active session or proposals');
        return;
      }

      setError(null);
      setIsProcessing(true);
      setFailedCommits([]);
      try {
        setPhase('committing');
        const committedChanges: OrejaCommittedChange[] = [];
        const failures: OrejaFailedCommit[] = [];

        for (const idx of selectedUpdatedIndexes) {
          const p = proposals.updated_tasks[idx];
          if (!p) continue;
          try {
            const change = await pushUpdatedTask(p);
            committedChanges.push(change);
          } catch (pushErr) {
            console.error(`[Oreja] Failed to push update for task ${p.clickup_task_id}:`, pushErr);
            failures.push({
              type: 'update',
              index: idx,
              title: p.task_title,
              error: pushErr instanceof Error ? pushErr.message : 'Unknown error',
            });
          }
        }

        for (const idx of selectedNewIndexes) {
          const p = proposals.new_tasks[idx];
          if (!p) continue;
          try {
            const change = await pushNewTask(p);
            committedChanges.push(change);
          } catch (createErr) {
            console.error(`[Oreja] Failed to create task "${p.title}":`, createErr);
            failures.push({
              type: 'new_task',
              index: idx,
              title: p.title,
              error: createErr instanceof Error ? createErr.message : 'Unknown error',
            });
          }
        }

        setFailedCommits(failures);

        await supabase
          .from('oreja_sessions')
          .update({ phase: 'committed', committed_changes: committedChanges })
          .eq('id', sessionId);

        setSession((prev) =>
          prev ? { ...prev, phase: 'committed', committed_changes: committedChanges } : prev,
        );
        setPhase('committed');

        trackFeatureEvent('oreja', FeatureEvent.OREJA.SESSION_COMMITTED, {
          tasks_updated: selectedUpdatedIndexes.length,
          tasks_created: selectedNewIndexes.length,
          tasks_failed: failures.length,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unexpected error occurred');
        console.error('[Oreja] commit() error:', err);
      } finally {
        setIsProcessing(false);
      }
    },
    [proposals, pushNewTask],
  );

  /**
   * Retries only the previously failed commits using the current proposals state.
   * Successes are moved to committedChanges; remaining failures stay in failedCommits.
   */
  const retryFailed = useCallback(async () => {
    const sessionId = sessionIdRef.current;
    if (!sessionId || !proposals || failedCommits.length === 0) return;

    setError(null);
    setIsProcessing(true);
    try {
      const successes: OrejaCommittedChange[] = [];
      const remainingFailures: OrejaFailedCommit[] = [];

      for (const failed of failedCommits) {
        try {
          if (failed.type === 'update') {
            const p = proposals.updated_tasks[failed.index];
            if (!p) continue;
            const change = await pushUpdatedTask(p);
            successes.push(change);
          } else {
            const p = proposals.new_tasks[failed.index];
            if (!p) continue;
            const change = await pushNewTask(p);
            successes.push(change);
          }
        } catch (retryErr) {
          console.error(`[Oreja] Retry failed for "${failed.title}":`, retryErr);
          remainingFailures.push({
            ...failed,
            error: retryErr instanceof Error ? retryErr.message : 'Unknown error',
          });
        }
      }

      setFailedCommits(remainingFailures);

      // Append newly succeeded items to session's committed_changes
      if (successes.length > 0) {
        setSession((prev) => {
          if (!prev) return prev;
          const updated = [...(prev.committed_changes ?? []), ...successes];
          return { ...prev, committed_changes: updated };
        });

        // Persist to DB
        const currentChanges = session?.committed_changes ?? [];
        const allChanges = [...currentChanges, ...successes];
        await supabase
          .from('oreja_sessions')
          .update({ committed_changes: allChanges })
          .eq('id', sessionId);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      console.error('[Oreja] retryFailed() error:', err);
    } finally {
      setIsProcessing(false);
    }
  }, [proposals, failedCommits, session, pushNewTask]);

  // Retry is possible when the transcript is already available (analyzing+)
  // but not for early phases where the audio blob has been consumed.
  // Committing failures are handled by retryFailed instead.
  const canRetry =
    !!error &&
    !!transcript &&
    !!sessionIdRef.current &&
    !!clickupListIdRef.current &&
    phase !== 'recording' &&
    phase !== 'uploading' &&
    phase !== 'transcribing' &&
    phase !== 'committing';

  /**
   * Retries the last failed operation based on the current phase and available state.
   * For early phases (uploading/transcribing) where the audio blob is lost, use reset() instead.
   */
  const retry = useCallback(async () => {
    const sessionId = sessionIdRef.current;
    const clickupListId = clickupListIdRef.current;
    if (!sessionId || !clickupListId || !transcript) return;

    setError(null);
    setIsProcessing(true);

    try {
      if (phase === 'analyzing' || phase === 'clarifying') {
        // Re-run the clarify edge call with the existing transcript
        setPhase('analyzing');

        const existingTasks = await fetchExistingTasks(clickupListId);
        const projectContext = {
          clientName: clientNameRef.current,
          projectName: projectNameRef.current,
          userName: userNameRef.current,
          listId: clickupListId,
        };

        const clarifyResponse = await callEdgeFunctionJson<{
          success: boolean;
          error?: string;
          data?: { questions: OrejaClarificationQuestion[] };
        }>('oreja-analyze', {
          body: { phase: 'clarify', sessionId, transcript, existingTasks, projectContext },
        });

        if (!clarifyResponse.success) {
          throw new Error(clarifyResponse.error ?? 'Clarification phase failed');
        }

        const retryQuestions = clarifyResponse.data?.questions ?? [];
        setQuestions(retryQuestions);

        await supabase
          .from('oreja_sessions')
          .update({ phase: 'clarifying', transcript, questions: retryQuestions })
          .eq('id', sessionId);

        setPhase('clarifying');
      } else if (phase === 'answering' || phase === 'proposing') {
        // Re-run the propose edge call
        setPhase('proposing');

        const existingTasks = await fetchExistingTasks(clickupListId);
        const projectContext = {
          clientName: clientNameRef.current,
          projectName: projectNameRef.current,
          userName: userNameRef.current,
          listId: clickupListId,
        };

        const proposeResponse = await callEdgeFunctionJson<{
          success: boolean;
          error?: string;
          data?: OrejaProposals;
        }>('oreja-analyze', {
          body: {
            phase: 'propose',
            sessionId,
            transcript,
            answers: '',
            existingTasks,
            projectContext,
          },
        });

        if (!proposeResponse.success) {
          throw new Error(proposeResponse.error ?? 'Proposal generation failed');
        }

        const retryProposals = normalizeProposals(proposeResponse.data);
        setProposals(retryProposals);
        await supabase
          .from('oreja_sessions')
          .update({ phase: 'reviewing', proposals: retryProposals })
          .eq('id', sessionId);
        setPhase('reviewing');
      } else if (phase === 'refining') {
        // For refining errors, fall back to reviewing with existing proposals
        setPhase('reviewing');
      } else {
        // Unexpected phase - fall back to reset
        reset();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Retry failed');
      console.error('[Oreja] retry() error:', err);
    } finally {
      setIsProcessing(false);
    }
  }, [phase, transcript, reset]);

  return {
    session,
    phase,
    transcript,
    questions,
    proposals,
    error,
    isProcessing,
    clickupListId: clickupListIdRef.current,
    failedCommits,
    canRetry,
    start,
    sendAnswers,
    skipClarification,
    startRefine,
    refine,
    updateProposals,
    commit,
    retryFailed,
    retry,
    reset,
  };
}
